/**
 * Retirement Planning Store (Zustand)
 *
 * Q: Why is this file called "retirementSTORE"?
 * A: This follows Zustand's naming convention. Zustand stores are global state management
 *    containers, similar to Redux stores but with simpler API. The "store" suffix indicates
 *    this file exports a Zustand store hook (useRetirementStore).
 *
 * Architecture:
 * - Settings (user inputs) → SettingsContext (src/contexts/SettingsContext.tsx)
 * - Calculations (projections) → This store (retirement planning calculations)
 * - UI state (modals, forms) → Component state or Zustand stores
 *
 * This store specifically handles retirement planning calculations and projections,
 * while user settings are managed separately in SettingsContext with age-based defaults.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import BigNumber from "bignumber.js";

/**
 * ARCHITECTURE NOTE: Settings vs Store
 *
 * We now have a SettingsContext (src/contexts/SettingsContext.tsx) that centralizes
 * all user settings with age-based fallback defaults. However, this RetirementData
 * interface and defaultData are STILL NEEDED for the following reasons:
 *
 * 1. Zustand Store Initialization:
 *    The store needs initial state when first created, before any user interaction.
 *    These defaults provide sensible starting values for the calculation engine.
 *
 * 2. Test Infrastructure:
 *    Tests require stable, predictable baseline data for calculations.
 *    These defaults are used by createTestStore() in test utilities.
 *
 * 3. Calculation API:
 *    The calculateRetirement() function accepts RetirementData directly,
 *    allowing calculations with any data set (not tied to user settings).
 *
 * 4. Separation of Concerns:
 *    - SettingsContext: User settings with Firebase persistence & age-based defaults
 *    - RetirementStore: Calculation engine with projection results
 *    - These can be integrated but remain separate layers
 *
 * Future Enhancement:
 * The store's calculate() function could read from SettingsContext instead of
 * internal state, further decoupling user settings from calculation logic.
 */

/**
 * Individual debt information for tracking
 */
export interface DebtInfo {
  balance: number;
  interestRate: number; // Annual rate as decimal (e.g., 0.05 for 5%)
  monthlyPayment: number;
}

/**
 * Collection of all debts
 */
export interface DebtsData {
  mortgage: DebtInfo;
  creditCard: DebtInfo;
  carLoan: DebtInfo;
  lineOfCredit: DebtInfo;
  studentLoan: DebtInfo;
  otherLiabilities: DebtInfo;
}

export interface RetirementData {
  currentAge: number;
  retirementAge: number;
  lifeExpectancy: number;
  currentSavings: number;
  monthlyContribution: number;
  annualReturn: number;
  inflationRate: number;
  monthlyExpenses: number;
  expensesAtRetirement: number; // Decimal change in expenses at retirement (e.g., -0.20 for 20% reduction, 0.10 for 10% increase)
  currency: string;
  debts: DebtsData; // Debt balances, interest rates, and payments
}

export interface RetirementResult {
  finalAmount: number;
  realValue: number;
  monthlyIncome: number;
  yearsToRetirement: number;
  readinessScore: number; // Composite readiness score (0-100) based on multiple factors
  shortTermHealthScore: number; // Short-term financial health (0-100)
  liquidReservesScore: number; // Emergency fund adequacy (0-100)
  cashFlowMarginScore: number; // Cash flow margin/buffer (0-100)
  lifestyleScore: number; // Lifestyle sustainability (0-100)
  savingsRate: number | undefined;
  incomeCoverage: number; // Income coverage ratio (1.0 = 100%)
}

export interface RetirementStore {
  data: RetirementData;
  result: RetirementResult;
  setData: (data: Partial<RetirementData>) => void;
  calculate: () => void;
}

/**
 * Factory function that creates the Zustand store state/actions.
 * Used by both the production singleton and test store instances.
 *
 * @param initialData - Initial retirement data (defaults to defaultData)
 */
export const createRetirementStoreState =
  (initialData: RetirementData = defaultData) =>
  (
    set: (partial: Partial<RetirementStore>) => void,
    get: () => RetirementStore
  ): RetirementStore => ({
    data: initialData,
    result: calculateRetirement(initialData),
    setData: (newData) => {
      const updatedData = { ...get().data, ...newData };
      set({ data: updatedData });
      get().calculate();
    },
    calculate: () => {
      const result = calculateRetirement(get().data);
      set({ result });
    },
  });

/**
 * Default Retirement Data
 *
 * These defaults serve as baseline values for:
 * 1. Zustand store initialization (before user data loads)
 * 2. Test suite baseline (stable, predictable values)
 * 3. Calculation API defaults (when called programmatically)
 *
 * Note: User-specific settings are managed in SettingsContext with
 * age-based statistical defaults. These store defaults are separate
 * and serve the calculation engine layer.
 */
const defaultData: RetirementData = {
  currentAge: 35,
  retirementAge: 67,
  lifeExpectancy: 90,
  currentSavings: 50000,
  monthlyContribution: 1500,
  annualReturn: 0.065, // 6.5% stored as decimal
  inflationRate: 0.025, // 2.5% stored as decimal
  monthlyExpenses: 4500,
  expensesAtRetirement: 0, // 0% change - maintain same expense level
  currency: "CAD",
  debts: {
    mortgage: { balance: 0, interestRate: 0, monthlyPayment: 0 },
    creditCard: { balance: 0, interestRate: 0, monthlyPayment: 0 },
    carLoan: { balance: 0, interestRate: 0, monthlyPayment: 0 },
    lineOfCredit: { balance: 0, interestRate: 0, monthlyPayment: 0 },
    studentLoan: { balance: 0, interestRate: 0, monthlyPayment: 0 },
    otherLiabilities: { balance: 0, interestRate: 0, monthlyPayment: 0 },
  },
};

/**
 * Helper: Track debt balances over time with amortization
 * Returns an object tracking which debts are paid off at each month.
 * Uses plain JavaScript numbers for deterministic behavior.
 */
interface DebtTracker {
  [debtName: string]: {
    balance: number;
    paid: boolean;
  };
}

/**
 * Initialize debt tracker from debts data
 */
function initializeDebtTracker(debts: DebtsData): DebtTracker {
  return {
    mortgage: { balance: debts.mortgage.balance, paid: false },
    creditCard: { balance: debts.creditCard.balance, paid: false },
    carLoan: { balance: debts.carLoan.balance, paid: false },
    lineOfCredit: { balance: debts.lineOfCredit.balance, paid: false },
    studentLoan: { balance: debts.studentLoan.balance, paid: false },
    otherLiabilities: { balance: debts.otherLiabilities.balance, paid: false },
  };
}

/**
 * Process one month of debt payments
 * Returns the total actual debt payments made this month (may be less than scheduled if debt paid off)
 */
function processMonthlyDebtPayments(
  debtTracker: DebtTracker,
  debts: DebtsData
): number {
  let totalPayments = 0;

  const debtEntries: Array<[string, keyof DebtsData]> = [
    ["mortgage", "mortgage"],
    ["creditCard", "creditCard"],
    ["carLoan", "carLoan"],
    ["lineOfCredit", "lineOfCredit"],
    ["studentLoan", "studentLoan"],
    ["otherLiabilities", "otherLiabilities"],
  ];

  for (const [trackerKey, debtKey] of debtEntries) {
    const debt = debtTracker[trackerKey];
    const debtInfo = debts[debtKey];

    if (debt.paid || debt.balance <= 0) {
      debt.paid = true;
      continue;
    }

    // Apply monthly interest
    const monthlyInterestRate = new BigNumber(debtInfo.interestRate).div(12);
    const monthlyGrowthFactor = monthlyInterestRate.plus(1);
    debt.balance = new BigNumber(debt.balance)
      .times(monthlyGrowthFactor)
      .toNumber();

    // Apply payment
    const payment = Math.min(debtInfo.monthlyPayment, debt.balance);
    debt.balance = new BigNumber(debt.balance).minus(payment).toNumber();
    totalPayments = new BigNumber(totalPayments).plus(payment).toNumber();

    // Mark as paid if balance is zero or negative
    if (debt.balance <= 0) {
      debt.balance = 0;
      debt.paid = true;
    }
  }

  return totalPayments;
}

/**
 * Year-by-year snapshot produced by the unified simulation.
 * Both calculatePortfolioAtRetirement and calculateImprovedReadinessScore
 * consume this timeline instead of independently simulating.
 */
export interface YearSnapshot {
  /** Year index (0-based, 0 = current year) */
  year: number;
  /** Whether the person is still working during this year */
  isWorking: boolean;
  /** Portfolio value at end of year (plain number to avoid Decimal allocation overhead) */
  portfolioValue: number;
  /** Monthly income during this year */
  monthlyIncome: number;
  /** Monthly expenses during this year (working or retirement) */
  monthlyExpenses: number;
  /** Accumulated liquid reserves (emergency fund) */
  liquidReserves: number;
}

/**
 * Simulate retirement year-by-year.
 *
 * This is the single source of truth for the month-by-month simulation.
 * Both portfolio projection and readiness scoring consume its output.
 *
 * Business Logic:
 * - Working years: monthly contribution + debt payments + compound interest
 * - Retirement years: withdraw adjusted expenses, apply annual growth
 * - Liquid reserves modeled at 20% of monthly savings, capped at 6 months
 *
 * @param data - Retirement planning data
 * @returns Array of year-by-year snapshots covering working + retirement years
 */
export function simulateRetirement(data: RetirementData): YearSnapshot[] {
  const yearsToRetirement = data.retirementAge - data.currentAge;
  const yearsInRetirement = data.lifeExpectancy - data.retirementAge;
  const totalYears = yearsToRetirement + yearsInRetirement;

  if (totalYears <= 0) return [];

  const monthlyRate = data.annualReturn / 12;
  const monthlyRatePlusOne = new BigNumber(1).plus(monthlyRate).toNumber();
  const retirementMonthlyExpenses = new BigNumber(data.monthlyExpenses)
    .times(new BigNumber(1).plus(data.expensesAtRetirement))
    .toNumber();
  const emergencyFundAllocationRate = 0.2;

  let portfolioValue = data.currentSavings;
  let liquidReserves = 0;
  const debtTracker = initializeDebtTracker(data.debts);
  const snapshots: YearSnapshot[] = [];

  // Calculate total scheduled debt payments (sum of all monthly payments).
  // When a debt is paid off, its actual payment drops to zero; the difference
  // between scheduled and actual payments represents freed-up cash flow that
  // should be redirected to portfolio contributions.
  const scheduledDebtPayments = new BigNumber(
    data.debts.mortgage.monthlyPayment
  )
    .plus(data.debts.creditCard.monthlyPayment)
    .plus(data.debts.carLoan.monthlyPayment)
    .plus(data.debts.lineOfCredit.monthlyPayment)
    .plus(data.debts.studentLoan.monthlyPayment)
    .plus(data.debts.otherLiabilities.monthlyPayment)
    .toNumber();

  for (let year = 0; year < totalYears; year++) {
    const isWorking = year < yearsToRetirement;
    const currentMonthlyExpenses = isWorking
      ? data.monthlyExpenses
      : retirementMonthlyExpenses;
    let monthlyIncome: number;

    if (isWorking) {
      monthlyIncome = data.monthlyContribution + data.monthlyExpenses;

      // Build liquid reserves during working years
      if (data.monthlyContribution > 0) {
        const recommendedEmergencyFund = new BigNumber(data.monthlyExpenses)
          .times(6)
          .toNumber();
        const annualAddition = new BigNumber(data.monthlyContribution)
          .times(emergencyFundAllocationRate)
          .times(12)
          .toNumber();
        liquidReserves = Math.min(
          liquidReserves + annualAddition,
          recommendedEmergencyFund
        );
      }

      // Month-by-month portfolio growth with debt tracking.
      // When debts are paid off, the freed-up cash flow (scheduled minus
      // actual payments) is redirected to additional portfolio contributions.
      for (let month = 0; month < 12; month++) {
        const actualDebtPayments = processMonthlyDebtPayments(
          debtTracker,
          data.debts
        );
        const freedCashFlow = new BigNumber(scheduledDebtPayments)
          .minus(actualDebtPayments)
          .toNumber();
        portfolioValue = new BigNumber(portfolioValue)
          .plus(data.monthlyContribution)
          .plus(freedCashFlow)
          .times(monthlyRatePlusOne)
          .toNumber();
      }
    } else {
      monthlyIncome = retirementMonthlyExpenses;
      const annualWithdrawal = new BigNumber(retirementMonthlyExpenses)
        .times(12)
        .toNumber();
      portfolioValue = new BigNumber(portfolioValue)
        .minus(annualWithdrawal)
        .times(new BigNumber(1).plus(data.annualReturn))
        .toNumber();

      if (portfolioValue <= 0) {
        monthlyIncome = 0;
        portfolioValue = 0;
      }
    }

    snapshots.push({
      year,
      isWorking,
      portfolioValue,
      monthlyIncome,
      monthlyExpenses: currentMonthlyExpenses,
      liquidReserves,
    });
  }

  return snapshots;
}

/**
 * Calculate improved readiness score from a pre-computed simulation timeline.
 *
 * Components:
 * 1. Short-Term Financial Health (30%) — ability to meet regular obligations
 * 2. Liquid Reserves (25%) — emergency fund adequacy
 * 3. Cash Flow Margin (25%) — buffer for irregular expenses
 * 4. Lifestyle Sustainability (20%) — discretionary spending capacity
 *
 * @param snapshots Year-by-year simulation timeline
 * @returns Object with composite score and component scores (all 0-100 scale)
 */
const calculateImprovedReadinessScore = (
  snapshots: YearSnapshot[]
): {
  compositeScore: number;
  shortTermHealthScore: number;
  liquidReservesScore: number;
  cashFlowMarginScore: number;
  lifestyleScore: number;
} => {
  const totalYears = snapshots.length;

  if (totalYears === 0) {
    return {
      compositeScore: 100,
      shortTermHealthScore: 100,
      liquidReservesScore: 100,
      cashFlowMarginScore: 100,
      lifestyleScore: 100,
    };
  }

  const shortTermCoverages: number[] = [];
  const liquidReservesScores: number[] = [];
  const marginPercentages: number[] = [];
  const lifestyleCapacities: number[] = [];

  for (const snap of snapshots) {
    const { monthlyIncome, monthlyExpenses, liquidReserves } = snap;

    // Component 1: Short-Term Financial Health
    let shortTermCoverage: number;
    if (monthlyExpenses === 0) {
      shortTermCoverage = 1.0;
    } else {
      const coverageRatio = monthlyIncome / monthlyExpenses;
      shortTermCoverage = Math.max(-1, Math.min(1, coverageRatio - 1));
    }
    shortTermCoverages.push(shortTermCoverage);

    // Component 2: Liquid Reserves Score
    const recommendedEmergencyFund = new BigNumber(monthlyExpenses)
      .times(6)
      .toNumber();
    const emergencyFundRatio =
      recommendedEmergencyFund > 0
        ? liquidReserves / recommendedEmergencyFund
        : 0;

    let yearlyLiquidReservesScore: number;
    if (emergencyFundRatio >= 1.0) {
      yearlyLiquidReservesScore = 100;
    } else if (emergencyFundRatio >= 0.5) {
      yearlyLiquidReservesScore = new BigNumber(60)
        .plus(
          new BigNumber(40)
            .times(new BigNumber(emergencyFundRatio).minus(0.5))
            .div(0.5)
        )
        .toNumber();
    } else {
      yearlyLiquidReservesScore = new BigNumber(emergencyFundRatio)
        .times(120)
        .toNumber();
    }
    liquidReservesScores.push(yearlyLiquidReservesScore);

    // Component 3: Cash Flow Margin
    let marginPercentage: number;
    if (monthlyExpenses === 0) {
      marginPercentage = 100;
    } else {
      marginPercentage = new BigNumber(monthlyIncome)
        .minus(monthlyExpenses)
        .div(monthlyExpenses)
        .times(100)
        .toNumber();
      marginPercentage = Math.max(-50, Math.min(100, marginPercentage));
    }
    marginPercentages.push(marginPercentage);

    // Component 4: Lifestyle Sustainability
    const requiredWithMargin = monthlyExpenses * 1.1;
    const discretionaryAmount = Math.max(0, monthlyIncome - requiredWithMargin);
    const lifestyleCapacity =
      monthlyIncome > 0 ? discretionaryAmount / monthlyIncome : 0;
    lifestyleCapacities.push(lifestyleCapacity);
  }

  // Convert averages to 0-100 scores
  const avgShortTermCoverage =
    shortTermCoverages.reduce((a, b) => a + b, 0) / totalYears;
  const shortTermHealthScore = Math.round(
    new BigNumber(avgShortTermCoverage).plus(1).div(2).times(100).toNumber()
  );

  const liquidReservesScore = Math.round(
    liquidReservesScores.reduce((a, b) => a + b, 0) / totalYears
  );

  const avgMargin = marginPercentages.reduce((a, b) => a + b, 0) / totalYears;
  let cashFlowMarginScore: number;
  if (avgMargin >= 15) {
    cashFlowMarginScore = 100;
  } else if (avgMargin >= 10) {
    cashFlowMarginScore = 80 + ((avgMargin - 10) / 5) * 20;
  } else if (avgMargin >= 5) {
    cashFlowMarginScore = 60 + ((avgMargin - 5) / 5) * 20;
  } else if (avgMargin >= 0) {
    cashFlowMarginScore = 40 + (avgMargin / 5) * 20;
  } else {
    cashFlowMarginScore = 40 + (avgMargin / 10) * 40;
    cashFlowMarginScore = Math.max(0, cashFlowMarginScore);
  }
  cashFlowMarginScore = Math.round(cashFlowMarginScore);

  const avgLifestyleCapacity =
    lifestyleCapacities.reduce((a, b) => a + b, 0) / totalYears;
  let lifestyleScore: number;
  if (avgLifestyleCapacity >= 0.1) {
    lifestyleScore = 100;
  } else if (avgLifestyleCapacity >= 0.075) {
    lifestyleScore = 90 + ((avgLifestyleCapacity - 0.075) / 0.025) * 10;
  } else if (avgLifestyleCapacity >= 0.05) {
    lifestyleScore = 70 + ((avgLifestyleCapacity - 0.05) / 0.025) * 20;
  } else if (avgLifestyleCapacity >= 0.025) {
    lifestyleScore = 40 + ((avgLifestyleCapacity - 0.025) / 0.025) * 30;
  } else if (avgLifestyleCapacity > 0) {
    lifestyleScore = 20 + (avgLifestyleCapacity / 0.025) * 20;
  } else {
    lifestyleScore = 0;
  }
  lifestyleScore = Math.round(lifestyleScore);

  const compositeScore = Math.round(
    0.3 * shortTermHealthScore +
      0.25 * liquidReservesScore +
      0.25 * cashFlowMarginScore +
      0.2 * lifestyleScore
  );

  return {
    compositeScore,
    shortTermHealthScore,
    liquidReservesScore,
    cashFlowMarginScore,
    lifestyleScore,
  };
};

// @copilot: IMPORTANT.  THIS IS THE MOST CRUCIAL FUNCTION OF THE ENTIRE SYSTEM.
// This function (and called subfunctions, if any) MUST absolutely use
// ALL settings values, except for Name and Email.
// I want test coverage for EACH AND EVERY SETTING field, which I want tested INDEPENDENTLY against an otherwise
// stable set of field values, to assert two things:
// - the value produced by the function factors in that field
// - the value produced by the function with a different field value produces a different result
//
// I want to see an exhaustic test results matrix output to a temporary log file demonstrating the results of
// these tests.
//
// I want this function to be EASILY COMPREHENSIBLE and EASY TO REASON ABOUT, so it will need to
// split the calculation into several piecemeal calculations, again each of which is EASILY COMPREHENSIBLE and EASY TO REASON ABOUT.
//
// For maximum precision, I do NOT want any rounding to be done in calculations.  Leave the rounding to the UI when the values are displayed.
//
// Heavily document the calculations, explaining the business reasons why the calculations are the way they are.
//
// Because the calculations require a lot of work & time, the results must be memoized and change ONLY WHEN the underlying Settings change.

/**
 * Calculate adjusted monthly expenses at retirement
 *
 * Business Logic:
 * - Retirement expenses often differ from working years
 * - expensesAtRetirement allows modeling lifestyle changes:
 *   - Negative values: expenses decrease (e.g., -0.20 = 20% reduction)
 *   - Positive values: expenses increase (e.g., 0.10 = 10% increase)
 *   - Zero: maintain same expense level
 * - Common reductions: commuting costs, work clothing, mortgage payoff
 * - Common increases: healthcare, travel, leisure activities
 *
 * @param monthlyExpenses - Current monthly expenses during working years
 * @param expensesAtRetirement - Decimal change factor (e.g., -0.20, 0, 0.10)
 * @returns Adjusted monthly expenses in retirement
 */
function calculateRetirementMonthlyExpenses(
  monthlyExpenses: number,
  expensesAtRetirement: number
): number {
  return new BigNumber(monthlyExpenses)
    .times(new BigNumber(1).plus(expensesAtRetirement))
    .toNumber();
}

/**
 * Calculate savings rate percentage
 *
 * Business Logic:
 * - Measures what percentage of cash flow is saved vs spent
 * - Formula: (contribution / (contribution + expenses)) * 100
 * - Higher savings rate = faster retirement progress
 * - Undefined when both contribution and expenses are zero (no cash flow)
 *
 * @param monthlyContribution - Amount saved each month
 * @param monthlyExpenses - Amount spent each month
 * @returns Savings rate as percentage (0-100), or undefined if no cash flow
 */
function calculateSavingsRate(
  monthlyContribution: number,
  monthlyExpenses: number
): number | undefined {
  // No cash flow: savings rate is undefined
  if (monthlyContribution === 0 && monthlyExpenses === 0) {
    return undefined;
  }

  const totalMonthlySpend = new BigNumber(monthlyContribution).plus(
    monthlyExpenses
  );

  // Defensive: if total spend is zero somehow, return undefined
  if (totalMonthlySpend.isZero()) {
    return undefined;
  }

  // Calculate savings rate as percentage
  // Note: No rounding - delegate to UI layer for display formatting
  return new BigNumber(monthlyContribution)
    .div(totalMonthlySpend)
    .times(100)
    .toNumber();
}

/**
 * Calculate income coverage ratio
 *
 * Business Logic:
 * - Measures how well retirement income covers retirement expenses
 * - Ratio = monthly income / retirement expenses
 * - 1.0 (100%) = income exactly covers expenses
 * - > 1.0 = surplus (e.g., 1.5 = 50% surplus)
 * - < 1.0 = shortfall (e.g., 0.8 = 20% shortfall)
 * - When expenses are zero, defaults to 1.0 (fully covered)
 *
 * @param monthlyIncome - Expected monthly income in retirement
 * @param retirementExpenses - Expected monthly expenses in retirement
 * @returns Coverage ratio (1.0 = 100% coverage)
 */
function calculateIncomeCoverage(
  monthlyIncome: BigNumber,
  retirementExpenses: number
): number {
  // When expenses are zero, income automatically covers 100%
  if (retirementExpenses === 0) {
    return 1.0;
  }

  // Calculate coverage ratio
  // Note: No rounding - delegate to UI layer for display formatting
  return monthlyIncome.div(retirementExpenses).toNumber();
}

/**
 * Calculate retirement projections based on current data
 *
 * This is the main calculation engine for retirement planning. It projects:
 * 1. Portfolio value at retirement (compound growth with contributions)
 * 2. Monthly retirement income (based on expense-based withdrawal strategy)
 * 3. Savings rate (percentage of cash flow saved)
 * 4. Income coverage (how well retirement income covers expenses)
 * 5. Readiness scores (financial health indicators)
 *
 * Uses the unified simulateRetirement() timeline so that portfolio projection
 * and readiness scoring share a single simulation (F2 consolidation).
 *
 * All calculations use BigNumber.js for precision (no floating-point errors).
 * No rounding is performed - UI layer handles display formatting.
 *
 * @param data - Complete retirement planning data
 * @returns Comprehensive retirement projections
 *
 * @internal - Exported for testing purposes only
 */
export const calculateRetirement = (data: RetirementData): RetirementResult => {
  const yearsToRetirement = data.retirementAge - data.currentAge;

  // Run the unified year-by-year simulation
  const snapshots = simulateRetirement(data);

  // Portfolio value at retirement is taken from the last working-year snapshot.
  // If there are no working years the portfolio value is the starting savings.
  const lastWorkingSnapshot = [...snapshots].reverse().find((s) => s.isWorking);
  const portfolioValueAtRetirement = new BigNumber(
    lastWorkingSnapshot
      ? lastWorkingSnapshot.portfolioValue
      : data.currentSavings
  );

  // Calculate adjusted retirement expenses (accounts for lifestyle changes)
  const retirementMonthlyExpenses = calculateRetirementMonthlyExpenses(
    data.monthlyExpenses,
    data.expensesAtRetirement
  );

  // Monthly income in retirement is based on covering adjusted expenses
  const monthlyIncome = new BigNumber(retirementMonthlyExpenses);

  // Calculate savings rate (percentage of cash flow saved during working years)
  const savingsRate = calculateSavingsRate(
    data.monthlyContribution,
    data.monthlyExpenses
  );

  // Calculate income coverage (how well retirement income covers expenses)
  const incomeCoverage = calculateIncomeCoverage(
    monthlyIncome,
    retirementMonthlyExpenses
  );

  // Calculate comprehensive readiness scores from the shared simulation
  const readinessScores = calculateImprovedReadinessScore(snapshots);

  // Convert fractional years to whole months for integer exponent (born Jan 1
  // assumption). floor(years * 12) preserves months from fractional years,
  // e.g. 30.5 years → 366 months, not 360.
  // Monthly inflation factor: (1 + annual_rate)^(1/12), computed via native
  // Math.pow (which handles fractional exponents) then wrapped in BigNumber.
  const monthsToRetirement = Math.floor(
    new BigNumber(yearsToRetirement).times(12).toNumber()
  );
  const monthlyInflationFactor = new BigNumber(
    Math.pow(1 + data.inflationRate, 1 / 12)
  );

  return {
    finalAmount: portfolioValueAtRetirement.toNumber(),
    // Inflation-adjusted value: what today's dollars the portfolio would be worth at retirement
    realValue: portfolioValueAtRetirement
      .div(monthlyInflationFactor.pow(monthsToRetirement))
      .toNumber(),
    monthlyIncome: monthlyIncome.toNumber(),
    yearsToRetirement,
    readinessScore: readinessScores.compositeScore,
    shortTermHealthScore: readinessScores.shortTermHealthScore,
    liquidReservesScore: readinessScores.liquidReservesScore,
    cashFlowMarginScore: readinessScores.cashFlowMarginScore,
    lifestyleScore: readinessScores.lifestyleScore,
    savingsRate,
    incomeCoverage,
  };
};

/**
 * Main retirement store for the application
 * Uses persist middleware for localStorage persistence.
 *
 * For testing, use createRetirementStoreState() to create isolated
 * store instances that don't share state with the production singleton.
 */
export const useRetirementStore = create<RetirementStore>()(
  persist(createRetirementStoreState(), {
    name: "retirement-storage",
  })
);

/**
 * Export defaultData for testing
 */
export { defaultData };
